---
title: How Blog Posts Work
coverImage: /blog/assets/images/mdsvex.avif
date: 2023-09-26T02:32:00.000Z
excerpt: How to manage existing blog posts and create new ones
hidden: false
tags:
  - Blog
  - Documentation
---

<script>
	import Callout from "@lib/components/atoms/Callout.svelte"; import CodeBlock from
	"@lib/components/molecules/CodeBlock.svelte"; import Image from "@lib/components/atoms/Image.svelte";
</script>

All posts are located inside the `assets/md` folder. Each folder has markdown files that describe a blog article or project devlog.

All posts are Markdown files, which means you can use the [Markdown syntax](https://www.markdownguide.org/basic-syntax) in them, and it will work out of the box. However, since this projects uses [MDsveX](https://mdsvex.pngwn.io/) to parse Markdown, you can also use Svelte components inside them! This means that the components used in other pages can also be used in blog posts.

<Callout type="info">This is a Svelte component inside a Markdown file!</Callout>

## Processing

Besides the blog post page itself, the blog posts can be displayed in other places, such as the `/blog` page, which lists all blog posts in the `<BlogCardScroller>`, and the `<BlogCardGrid>` component, which is used in the home page.

The blog posts are first processed by Mdsvex, which converts them into svelte modules, which are then imported in `src/lib/fetchers/[posts and projects]`. They are stored in Svelte stores so any component that needs them can access the data, and since the import is asynchronous, any component that needs them uses an `{#await postStore.items.load()}` to get the preprocessed markdown components. This await should be instant for all but the first component that requests posts.

I added SPA routing, so after a user clicks on one in the grid or scroller, or navigates to a blog link, the router gets the blog id from the url and tells the (blogs or projects) page to mount the correct article.
The posts have metadata defined in the frontmatter header, which contains things like tags and an excerpt. Us

## Creating a new post

To create a new post, create a new folder inside the `assets/md` folders, and inside it, create an `article.mdx` file. The blog's url page name, under `blog/[articles or projects]`, is defined in the frontmatter.

The markdown files begin with a frontmatter header, which is a YAML-like syntax that is used to define metadata for the post, separated from the rest of the content by three dashes (`---`). An example of a front matter is:

<CodeBlock filename="article.mdx" lang="markdown">

```md
---
title: My New Blog Post
date: 2023-04-22T20:45:25.350Z
excerpt: A short description of the post
coverImage: /images/cover-image.jpg
hidden: true
tags:
  - Example
---
```

</CodeBlock>

## Components

The components are organized following the [Atomic Design](https://medium.com/@WeAreMobile1st/atomic-design-getting-started-916bc81bad0e) principles, albeit somewhat simplified. Components are in the `src/lib/components` folder, and are organized in the following way:

### Atoms

Atoms are the most basic components, which can be seen as building blocks for other components. They're small and self-contained, and do only one thing. Examples of atoms are buttons, inputs, and tags.

### Molecules

Molecules are groups of atoms that work together to form a more complex component. Examples of molecules are cards, groups of links, or an alert callout.

### Organisms

Organisms, in this project, are code blocks that represent a section of a page, such as the header, footer and hero section. They can be used directly in a page as a sort of building block, so the page's code can be as simple as this:

```html:Page.svelte
<Page>
	<div slot="header-after" />
	<div slot="left-sidebar" />
	<div slot="right-sidebar" />
	<main slot="main-content">
    <Hero>
      <h1 class="hello">Hello, I'm Jack Bellinger</h1>
      <p>I'm an ex-Amazon Software Engineer (Looking for a job!). I post my side projects here.</p>
      <div class="intro">
        Send me a message!
        <Socials />
      </div>
    </Hero>
    <BlogCardGrid
      posts={projects.filter((proj) => !proj.hidden)}
      title={'Projects'}
      description={"Here's what I'm working on!"}
      numToShow={howManyRecent}
    />
    <BlogCardGrid posts={posts.filter((post) => !post.hidden)} title={'Articles'} numToShow={howManyRecent} />
  </main>
</Page>
```

## Managing blog posts

I highly recommend the [Front Matter VS Code extension](https://frontmatter.codes/) to manage blog posts. It gives you a nice CMS-like UI to manage the front matter of all blog posts, as well as a preview of their content. It is, of course, optional, and you can manage everything directly in the Markdown files if you prefer.

## RSS

This template automatically generates a RSS feed of your blog posts. It is generated in the `src/routes/rss.xml/+server.ts` file, and it is available at the `/rss.xml` URL.
